import React, { useEffect } from 'react';
import clsx from 'clsx';
import { useEditor } from '@craftjs/core';
import { TextField as InspectorTextField } from '@/components/inspector-fields/TextField/TextField';
import { useFormFieldHiddenSync } from '@/hooks/useFormFieldHiddenSync';
import { InfoTooltip } from '@/components/tooltips/InfoTooltip/InfoTooltip';
import IconClose from '@/assets/icons/icon-close.svg?react';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import { Switch } from '@/components/inspector-fields/Switch/Switch';
import type { FormEmailFieldProps as SharedFormEmailFieldProps } from '@builder4/shared/components/Form';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { FORM_EMAIL_FIELD_NODE_NAME, FIELD_LABEL_EMAIL } from '@/utils/constants';

interface EmailFieldProps {
  className?: string;
  title?: string;
  onClose?: () => void;
  /** The specific field node ID to edit */
  fieldNodeId: string;
  getDefaultFieldLabel: () => string;
}

export const EmailField: React.FC<EmailFieldProps> = ({
  className = '',
  title = 'Email Field',
  onClose,
  fieldNodeId,
  getDefaultFieldLabel,
}) => {
  const { actions, fieldProps } = useEditor((_, query) => {
    const node = query.node(fieldNodeId).get();
    return {
      fieldProps: (node?.data.props || {}) as SharedFormEmailFieldProps,
    };
  });

  const { setBPProp, getBPProp } = useBreakpointAwareProp(FORM_EMAIL_FIELD_NODE_NAME);

  // Read props using breakpoint-aware getter
  const label = getBPProp(fieldProps, 'label');
  const showLabel = getBPProp(fieldProps, 'showLabel');
  const placeholder = getBPProp(fieldProps, 'placeholder');
  const required = getBPProp(fieldProps, 'required');
  const hidden = getBPProp(fieldProps, 'hidden');
  const helpText = getBPProp(fieldProps, 'helpText');
  const showHelpText = getBPProp(fieldProps, 'showHelpText');
  const urlMapping = getBPProp(fieldProps, 'urlMapping');
  const primaryEmailField = getBPProp(fieldProps, 'primaryEmailField');

  // Throttled — batches rapid keystrokes (text inputs)
  const setProp = (callback: (props: SharedFormEmailFieldProps) => void) => {
    actions.history.throttle(500).setProp(fieldNodeId, callback);
  };

  // Direct — discrete actions (toggles, onBlur)
  const setPropDirect = (callback: (props: SharedFormEmailFieldProps) => void) => {
    actions.setProp(fieldNodeId, callback);
  };

  // Sync hidden prop with layer visibility
  const { handleHiddenChange } = useFormFieldHiddenSync<SharedFormEmailFieldProps>(
    fieldNodeId,
    hidden,
    setPropDirect,
  );

  useEffect(() => {
    actions.history.ignore().setCustom(fieldNodeId, (custom) => {
      custom.displayName = label || FIELD_LABEL_EMAIL;
    });
  }, [label, actions, fieldNodeId]);

  return (
    <div
      className={clsx(
        'bg-neutral-0 flex h-[480px] max-h-[480px] w-[248px] flex-col overflow-hidden rounded-lg shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] outline outline-neutral-100',
        className,
      )}
      data-craft-history
    >
      {/* Card header */}
      <div className="bg-neutral-0 inline-flex h-10 min-h-10 w-full items-center justify-between rounded-t-lg border border-neutral-100 px-3">
        <div className="text-xs leading-5 font-semibold text-neutral-900">{title}</div>
        <IconSimpleButton aria-label="Close" icon={IconClose} onClick={onClose} />
      </div>

      <div className="flex min-h-0 w-full flex-1 flex-col border border-t-0 border-neutral-100">
        <div className="no-scrollbar h-[440px] flex-1 overflow-y-auto">
          <div className="flex w-full flex-col items-start justify-start gap-3 px-3 pt-3 pb-4">
            {/* Field Label */}
            <InspectorTextField
              className="w-full"
              classNameInputParent="max-w-none"
              classNameInput="max-w-[140px]"
              text="Field Label"
              inputValue={label}
              placeholderText="Email"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setProp((props: SharedFormEmailFieldProps) => {
                  setBPProp(props, 'label', e.target.value);
                })
              }
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                if (!e.target.value) {
                  setProp((props: SharedFormEmailFieldProps) => {
                    setBPProp(props, 'label', getDefaultFieldLabel());
                  });
                }
              }}
            />

            {/* Placeholder */}
            <InspectorTextField
              className="w-full"
              classNameInputParent="max-w-none"
              classNameInput="max-w-[140px]"
              text="Placeholder"
              inputValue={placeholder}
              placeholderText="Enter your email"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setProp((props: SharedFormEmailFieldProps) => {
                  setBPProp(props, 'placeholder', e.target.value);
                })
              }
            />

            {/* Label toggle */}
            <Switch
              label="Show Label"
              checked={showLabel}
              onCheckedChange={(checked: boolean) =>
                setPropDirect((props: SharedFormEmailFieldProps) => {
                  setBPProp(props, 'showLabel', checked);
                })
              }
            />

            {/* Divider */}
            <div className="self-stretch pb-1">
              <div className="h-0 border-t border-neutral-100" />
            </div>

            {/* URL Mapping */}
            <div className="flex w-full flex-col gap-2">
              <div className="inline-flex items-center gap-1">
                <div className="text-button-sm leading-4 font-medium text-neutral-500">
                  URL Mapping
                </div>
                <InfoTooltip text="Pre-fill this field with data from your page URL. Enter the parameter name from your URL (like location or utm_source). For example, if you run ads targeting different cities with ?location=Chicago in the URL, enter location here and this field will automatically show 'Chicago' for visitors from that ad." />
              </div>
              <InspectorTextField
                className="w-full"
                classNameInputParent="w-full max-w-full"
                classNameInput="max-w-full"
                text=""
                inputValue={urlMapping || ''}
                placeholderText="(Optional)"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setProp((props: SharedFormEmailFieldProps) => {
                    setBPProp(props, 'urlMapping', e.target.value);
                  })
                }
              />
            </div>

            {/* Toggles */}
            <div className="flex w-full flex-col">
              {/* Hide required toggle for primary email field - it's always required */}
              {!primaryEmailField && (
                <Switch
                  label="This field is required"
                  checked={required}
                  onCheckedChange={(checked: boolean) =>
                    setPropDirect((props: SharedFormEmailFieldProps) => {
                      setBPProp(props, 'required', checked);
                    })
                  }
                />
              )}
              <Switch
                label="This is a hidden field"
                checked={hidden}
                onCheckedChange={handleHiddenChange}
              />
              <Switch
                label="Include help text"
                checked={showHelpText}
                onCheckedChange={(checked: boolean) =>
                  setPropDirect((props: SharedFormEmailFieldProps) => {
                    setBPProp(props, 'showHelpText', checked);
                  })
                }
              >
                <InspectorTextField
                  className="w-full"
                  classNameInputParent="max-w-none"
                  classNameInput="max-w-[160px]"
                  text="Text"
                  inputValue={helpText}
                  placeholderText="We only accept USDe.."
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setProp((props: SharedFormEmailFieldProps) => {
                      setBPProp(props, 'helpText', e.target.value);
                    })
                  }
                />
              </Switch>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailField;
